import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Lightbulb, EyeOff, PackageOpen, TreePine, Award, Trophy, Snowflake, PartyPopper } from "lucide-react";
import HolidayCanvas from "../components/HolidayCanvas";
import { Reveal, Overline } from "../components/Reveal";
import { IMAGES, PHONE, PHONE_TEL } from "../data";

const COLOR_MODES = [
  { id: "warm", label: "Warm White", swatch: "#FFD9A0", testid: "holiday-color-warm-white" },
  { id: "cool", label: "Cool White", swatch: "#DCEAFF", testid: "holiday-color-cool-white" },
  { id: "festive", label: "Red & Green", swatch: "linear-gradient(135deg,#EF4444,#10B981)", testid: "holiday-color-festive" },
  { id: "blue", label: "Royal Blue", swatch: "#3B82F6", testid: "holiday-color-royal-blue" },
  { id: "gold", label: "Gold Sparkle", swatch: "#D4AF37", testid: "holiday-color-gold" },
];

const FEATURES = [
  { icon: Scissors, title: "Custom Cut To Your Home", text: "Every strand is measured and cut on-site to fit your exact roofline, peaks, and ridges. No drooping extra length, ever." },
  { icon: Lightbulb, title: "Commercial-Grade Lighting", text: "Pro-grade bulbs and wiring built to shine all season long. No burnt-out bulbs halfway through December." },
  { icon: EyeOff, title: "No Wires. Ever.", text: "Hidden clips and concealed runs mean all anyone sees is the glow. Daytime clean, nighttime magic." },
  { icon: PackageOpen, title: "Free Takedown", text: "When the season wraps, we come back and take it all down at no cost. You never touch a ladder." },
];

const EXTRAS = [
  { icon: TreePine, title: "Bush & Tree Wrapping", text: "Trunks, branches, and shrubs wrapped in light for a full-property display." },
  { icon: Award, title: "Wreaths", text: "Lit, designer wreaths sized and hung for your entry, garage, and windows." },
  { icon: PartyPopper, title: "Yard Inflatables", text: "Installed, anchored, and lit so your yard scene looks intentional — not chaotic." },
];

function nextNov1() {
  const now = new Date();
  let target = new Date(now.getFullYear(), 10, 1, 0, 0, 0);
  if (now >= target) target = new Date(now.getFullYear() + 1, 10, 1, 0, 0, 0);
  return target;
}

function useCountdown() {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = nextNov1();
    const tick = () => {
      const diff = Math.max(0, target - new Date());
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return left;
}

export default function Holiday() {
  const [mode, setMode] = useState("warm");
  const { d, h, m, s } = useCountdown();

  return (
    <div data-testid="holiday-page" className="pt-20">
      {/* ANIMATED HERO */}
      <section data-testid="holiday-hero" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#141018_0%,#0A0B0E_60%)]" />
        <HolidayCanvas mode={mode} className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-obsidian to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel border border-gold/40 text-gold-light text-xs font-mono uppercase tracking-[0.25em] mb-8"
          >
            <Snowflake className="h-3.5 w-3.5 animate-twinkle" />
            Our Specialty · Holiday Lighting
          </motion.div>

          <h1 className="font-serif font-bold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl text-slate-100 max-w-4xl">
            {["SKIP THE LADDER.", "KEEP THE MAGIC."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-gold-gradient" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            Don't spend your December untangling strands on an icy roof — or miss the Christmas joy lights bring because it felt like too much. We design it, custom cut it, install it, and take it down free. You just enjoy the glow.
          </motion.p>

          {/* COUNTDOWN */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 inline-block rounded-2xl glass-panel border border-gold/30 p-6"
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-4">
              50% off installation ends November 1st
            </p>
            <div data-testid="holiday-countdown" className="flex gap-3 sm:gap-4">
              {[["Days", d], ["Hrs", h], ["Min", m], ["Sec", s]].map(([label, val]) => (
                <div key={label} className="text-center">
                  <div className="w-16 sm:w-20 py-3 rounded-xl bg-obsidian/80 border border-white/10 font-mono text-2xl sm:text-3xl font-bold text-gold-light">
                    {String(val).padStart(2, "0")}
                  </div>
                  <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/book?type=Holiday%20Lighting"
              data-testid="holiday-book-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5"
            >
              Lock In 50% Off
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={PHONE_TEL}
              data-testid="holiday-call-button"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-silver-light border border-silver/30 glass-panel transition-all duration-300 hover:border-gold/60 hover:text-gold-light hover:-translate-y-0.5"
            >
              {PHONE}
            </a>
          </motion.div>
        </div>
      </section>

      {/* COLOR PICKER */}
      <section className="border-y border-white/5 bg-charcoal/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-20 text-center">
          <Reveal>
            <Overline>Pick your palette</Overline>
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-100 tracking-tight mb-4">
              Watch the lights change
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-10 text-sm sm:text-base">
              Cool white, warm white, red, green, blue — or tell us your own idea and we'll build it. Tap a palette and watch this page's lights follow.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap justify-center gap-4">
              {COLOR_MODES.map((c) => (
                <button
                  key={c.id}
                  data-testid={c.testid}
                  onClick={() => setMode(c.id)}
                  className={`flex items-center gap-3 px-5 py-3 rounded-full border text-sm font-medium transition-all duration-300 ${
                    mode === c.id
                      ? "border-gold text-gold-light shadow-[0_0_20px_rgba(233,199,103,0.3)]"
                      : "border-white/10 text-slate-300 hover:border-gold/40 hover:-translate-y-0.5"
                  }`}
                >
                  <span className="h-4 w-4 rounded-full ring-1 ring-white/20" style={{ background: c.swatch }} />
                  {c.label}
                </button>
              ))}
              <Link
                to="/book?type=Holiday%20Lighting"
                data-testid="holiday-color-custom"
                className="flex items-center gap-3 px-5 py-3 rounded-full border border-dashed border-gold/50 text-sm font-medium text-gold hover:bg-gold/10 transition-all duration-300"
              >
                + Dream up your own
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
        <Reveal>
          <Overline>Why GlowMode holidays</Overline>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-slate-100 tracking-tight mb-14 max-w-2xl">
            The display your neighbors slow down for
          </h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-20">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div data-testid={`holiday-feature-${i}`} className="h-full rounded-2xl border border-white/5 bg-card2/60 p-8 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:-translate-y-1">
                <f.icon className="h-8 w-8 text-gold mb-5" />
                <h3 className="font-serif text-xl font-medium text-slate-100 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="grid gap-8 lg:grid-cols-2 items-center rounded-3xl border border-white/5 overflow-hidden bg-card2/40">
            <div className="relative h-72 lg:h-full min-h-[320px]">
              <img src={IMAGES.holidayTree} alt="Trees wrapped in Christmas lights" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card2/60 hidden lg:block" />
            </div>
            <div className="p-8 sm:p-12">
              <Overline>Complete the scene</Overline>
              <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-slate-100 mb-8">Available add-ons, installed for you</h3>
              <div className="space-y-6">
                {EXTRAS.map((x) => (
                  <div key={x.title} data-testid={`holiday-extra-${x.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex gap-4">
                    <x.icon className="h-6 w-6 text-gold shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-slate-100">{x.title} <span className="text-xs font-mono text-gold ml-1">EXTRA</span></p>
                      <p className="text-sm text-slate-400 mt-1">{x.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* PRICE BEAT */}
      <section className="border-t border-white/5 bg-gradient-to-b from-charcoal/60 to-obsidian">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28 text-center">
          <Reveal>
            <Trophy className="h-12 w-12 text-gold mx-auto mb-6" />
            <h2 className="text-3xl sm:text-5xl font-serif font-bold text-slate-100 tracking-tight mb-6">
              We beat every competitor's price. <span className="text-gold-gradient">Every time.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-10 text-base sm:text-lg leading-relaxed">
              Got a quote from another lighting company? Bring it to us. We'll beat it — with better materials, cleaner installs, and free takedown on top.
            </p>
            <Link
              to="/book?type=Holiday%20Lighting"
              data-testid="holiday-bottom-book-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-obsidian bg-gradient-to-br from-gold-light via-gold to-gold-dark transition-all duration-300 hover:shadow-[0_0_35px_rgba(233,199,103,0.5)] hover:-translate-y-0.5"
            >
              Book Before Nov 1st — Save 50%
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
